import { Unit } from './Unit.js';
import { TeamBaseAI } from '../ai/TeamBaseAI.js';
import {
  BASE_CONFIG, TEAM_CONFIG, RESOURCE_CONFIG, UI_CONFIG,
  THRESHOLD_CONFIG, ANIMATION_CONFIG, DEBUG_VISUALIZATION_CONFIG,
  RADIO_CONFIG
} from '../config.js';
import { Projectile } from './Projectile.js';
import { Soldier } from './Soldier.js';
import { Metal } from './Metal.js';
import { Crystal } from './Crystal.js';
import { Nebula } from './Nebula.js';
import { Corpse } from './Corpse.js';
import { getDistance } from '../utils/math.js';
import { audioManager } from '../systems/AudioManager.js';
import { isDebugMode, debugLog, debugError } from '../utils/debug.js';
import { teamRadioNetwork } from '../systems/unit/RadioSystem.js';
// soulforgeAPI imported dynamically to avoid circular dependencies

export class TeamBase extends Unit {
  constructor(x, y, team, canvas) {
    super(x, y, canvas, team);
    
    // Override Unit defaults with TeamBase-specific values
    this.size = BASE_CONFIG.SIZE;
    this.maxHealth = BASE_CONFIG.HEALTH;
    this.health = this.maxHealth;
    this.weaponRange = BASE_CONFIG.WEAPON_RANGE;
    this.damage = BASE_CONFIG.DAMAGE;
    this.level = BASE_CONFIG.STARTING_LEVEL; // Starting level for bases
    this.perceptionRange = BASE_CONFIG.PERCEPTION_RANGE;
    
    // Base is stationary
    this.speed = 0;
    this.vx = 0;
    this.vy = 0;
    
    // Base-specific properties
    this.fireRate = BASE_CONFIG.FIRE_RATE;
    this.missileKills = 0; // Track kills for perception range bonuses

    // Ammunition system
    this.maxAmmo = BASE_CONFIG.MAX_AMMO;
    this.ammo = BASE_CONFIG.STARTING_AMMO;
    this.lastUpdate = Date.now();

    // Priority management
    this.priority = 'hunt';
    this.targetResource = null; // Resource the base wants units to collect

    this.maxMetal = BASE_CONFIG.METAL_MAX;

    // Spawning
    this.spawnInterval = BASE_CONFIG.SPAWN_INTERVAL;
    this.lastSpawnTime = Date.now();

    // Combat handled by AI

    // Resources (start with ample metal, some organics to spawn initial units)
    this.resources = {
      metal: this.maxMetal,
      organics: RESOURCE_CONFIG.ORGANICS_PER_UNIT * 50 // Start with enough for 50 units (was 3)
    };
    this.crystalUnitsToSpawn = 0;
    
    // Async spawn tracking
    this.pendingSpawnRequest = null; // Promise for ongoing soul request
    this.pendingSpawnType = null; // 'organic' or 'crystal'
    
    // Create AI for team base
    this.ai = new TeamBaseAI(this);
    
    // Set initial state to match state machine
    this.setState('idle');
    
    // Spawn animation
    this.spawnScale = 0.0;
    this.spawnProgress = 0;
    this.spawnSpeed = ANIMATION_CONFIG.BASE_SPAWN_SPEED;
    this.isSpawning = true;
    
    // Resource delivery pulse
    this.deliveryPulse = 0;
    this.deliveryPulseDecay = ANIMATION_CONFIG.BASE_DELIVERY_PULSE_DECAY;
    
    // Track soldiers actively repairing this base
    this.activeRepairers = new Set();
    this.repairPulse = 0; // Visual effect for repair activity

    // Corpse tractor beam tracking
    this.corpsesInRange = new Map(); // Map<corpse, firstSeenTime>
    this.tractorBeamCorpses = []; // Active corpses being pulled (for rendering)

    // Resource tractor beam tracking
    this.tractorBeamResources = []; // Active resources being pulled (for rendering)

    // Defense request broadcasting
    this.lastDefenseRequestTime = 0; // Track last broadcast time
    this.activeDefenseRequest = null; // Current active defense request
  }
  
  // Override movement methods - bases don't move
  moveTowards(targetX, targetY, speedMultiplier = 1) {
    // Base is stationary
  }
  
  moveAway(fromX, fromY, speedMultiplier = 1) {
    // Base is stationary
  }
  
  updateMovement(delta) {
    // Base is stationary - no movement update needed
  }

  // Override update method for base-specific logic
  update(soldiers, projectiles, resources = [], canSpawnUnit = null) {
    if (!this.alive) return null;
    
    const now = Date.now();
    
    // Handle spawn animation
    if (this.isSpawning) {
      this.spawnProgress += this.spawnSpeed;
      if (this.spawnProgress >= 1.0) {
        this.spawnProgress = 1.0;
        this.isSpawning = false;
        this.spawnScale = 1.0;
      } else {
        // Elastic ease out for bouncy effect
        const t = this.spawnProgress;
        const c4 = (2 * Math.PI) / 3;
        this.spawnScale = t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
      }
    }
    
    // Decay delivery pulse
    if (this.deliveryPulse > 0) {
      this.deliveryPulse -= this.deliveryPulseDecay;
      if (this.deliveryPulse < 0) this.deliveryPulse = 0;
    }
    
    // Decay repair pulse
    if (this.repairPulse > 0) {
      this.repairPulse -= 0.02;
      if (this.repairPulse < 0) this.repairPulse = 0;
    }
    
    // Apply repairs from active repairers
    if (this.activeRepairers.size > 0 && this.health < this.maxHealth) {
      // Clean up invalid repairers
      for (const soldier of this.activeRepairers) {
        if (!soldier.alive || soldier.health <= 0 || getDistance(this, soldier) > BASE_CONFIG.REPAIR_RANGE) {
          this.activeRepairers.delete(soldier);
        }
      }
      
      // Apply cumulative repair from all active repairers
      if (this.activeRepairers.size > 0) {
        const repairPerFrame = (BASE_CONFIG.SOLDIER_REPAIR_RATE * this.activeRepairers.size) / 60; // Convert to per frame
        this.health = Math.min(this.maxHealth, this.health + repairPerFrame);
        this.repairPulse = Math.min(1.0, this.repairPulse + 0.1); // Visual feedback
        
        // Check if fully repaired
        if (this.health >= this.maxHealth && this.ai) {
          this.ai.onRepairComplete();
        }
      }
    }
    
    // Store references for AI
    this.projectiles = projectiles;
    
    // Let AI handle all perception, decisions, and resource conversion
    if (this.ai) {
      this.ai.updatePerception(soldiers, projectiles, resources, [this]);
      this.ai.makeDecision();
      this.ai.executeState(); // Execute current state logic
    }
    
    // Tractor beam resource collection
    this.tractorBeamResources = []; // Reset for this frame
    if (resources && resources.length > 0) {
      // Calculate tractor beam range as 90% of current perception range (scales with leveling)
      const tractorBeamRange = this.perceptionRange * 0.9;

      for (const resource of resources) {
        // Skip inactive resources
        if (!resource.active) continue;

        // Only tractor beam Metal and Crystal (not Nebula which must be mined, not Corpse which has own system)
        if (!(resource instanceof Metal) && !(resource instanceof Crystal)) continue;

        // Don't tractor resources assigned to our soldiers (en route)
        if (this.targetResource === resource) continue;

        // Don't interfere with active soldier collection animations
        if (resource.isBeingCollected) continue;

        const distance = getDistance(this, resource);
        if (distance <= tractorBeamRange && distance > BASE_CONFIG.SIZE) {
          // Pull resource toward base
          const dx = this.x - resource.x;
          const dy = this.y - resource.y;
          const pullStrength = BASE_CONFIG.TRACTOR_BEAM_SPEED * (1 - distance / tractorBeamRange);

          resource.x += (dx / distance) * pullStrength;
          resource.y += (dy / distance) * pullStrength;

          // Add to active beam list for rendering
          this.tractorBeamResources.push(resource);

          // If resource reaches base, collect it
          if (getDistance(this, resource) <= BASE_CONFIG.SIZE) {
            const result = resource.collect(this);
            this.depositResource(result.type, result.amount);
            resource.active = false;

            debugLog('Base', {
              action: 'tractor_beam_collected',
              team: this.team.name,
              resourceType: result.type,
              amount: result.amount
            });
          }
        }
      }
    }

    // Corpse tractor beam (harvest souls from fallen enemies)
    this.tractorBeamCorpses = []; // Reset for this frame
    if (resources && resources.length > 0) {
      // Clean up corpses that no longer exist
      for (const [corpse, time] of this.corpsesInRange.entries()) {
        if (!corpse.active || !resources.includes(corpse)) {
          this.corpsesInRange.delete(corpse);
        }
      }

      for (const resource of resources) {
        // Only pull corpses
        if (!resource.active || !(resource instanceof Corpse)) continue;

        const distance = getDistance(this, resource);

        // Track corpses within range
        if (distance <= BASE_CONFIG.CORPSE_TRACTOR_RANGE) {
          if (!this.corpsesInRange.has(resource)) {
            this.corpsesInRange.set(resource, now);
          }

          const timeInRange = now - this.corpsesInRange.get(resource);

          // Pull corpse toward base (visual effect)
          if (distance > BASE_CONFIG.SIZE) {
            const dx = this.x - resource.x;
            const dy = this.y - resource.y;
            const pullStrength = BASE_CONFIG.CORPSE_TRACTOR_SPEED * (1 - distance / BASE_CONFIG.CORPSE_TRACTOR_RANGE);

            resource.x += (dx / distance) * pullStrength;
            resource.y += (dy / distance) * pullStrength;

            // Add to active beam list for rendering
            this.tractorBeamCorpses.push(resource);
          }

          // Convert corpse to organics after delay
          if (timeInRange >= BASE_CONFIG.CORPSE_CONVERT_DELAY) {
            this.resources.organics += BASE_CONFIG.CORPSE_ORGANICS_VALUE;
            resource.active = false;
            this.corpsesInRange.delete(resource);

            debugLog('TeamBase', {
              action: 'corpse_harvested',
              team: this.team.name,
              organicsGained: BASE_CONFIG.CORPSE_ORGANICS_VALUE,
              totalOrganics: this.resources.organics,
              corpseLevel: resource.level
            });
          }
        } else {
          // Corpse left range, remove from tracking
          this.corpsesInRange.delete(resource);
        }
      }
    }
    
    // Ammo regeneration when critical
    if (BASE_CONFIG.ENABLE_AMMO_REGEN && this.ammo < this.maxAmmo * BASE_CONFIG.CRITICAL_AMMO_THRESHOLD) {
      const delta = now - this.lastUpdate;
      const ammoToAdd = BASE_CONFIG.AMMO_REGEN_RATE * (delta / 1000); // Convert to per second
      this.ammo = Math.min(this.ammo + ammoToAdd, this.maxAmmo);
    }

    // Emergency organic generation (last resort recovery)
    const soldierCount = soldiers.filter(s => s.alive && s.team.id === this.team.id).length;
    if (soldierCount <= BASE_CONFIG.CRITICAL_SOLDIER_COUNT &&
        this.resources.organics < RESOURCE_CONFIG.ORGANICS_PER_UNIT) {
      const delta = now - this.lastUpdate;
      const organicsToAdd = BASE_CONFIG.EMERGENCY_ORGANICS_RATE * (delta / 1000); // Convert to per second
      this.resources.organics = Math.min(
        this.resources.organics + organicsToAdd,
        RESOURCE_CONFIG.ORGANICS_PER_UNIT
      );

      // Debug log when emergency generation is active
      if (organicsToAdd > 0) {
        debugLog('TeamBase', {
          action: 'emergency_organics',
          team: this.team.name,
          organicsGained: organicsToAdd.toFixed(3),
          totalOrganics: this.resources.organics.toFixed(2),
          soldierCount: soldierCount
        });
      }
    }

    // Spawning logic
    let spawned = null;
    
    // Add spawn timer to prevent instant spawning of all units
    if (!this.lastActualSpawnTime) {
      this.lastActualSpawnTime = now;
    }

    // Check unit limits before spawning
    const canSpawn = canSpawnUnit ? canSpawnUnit() : true;
    
    // Debug logging for spawning
    if (this.resources.organics >= RESOURCE_CONFIG.ORGANICS_PER_UNIT || this.crystalUnitsToSpawn > 0) {
      // debugLog('Base', {
      //   action: 'spawn_check',
      //   team: this.team.name,
      //   state: this.state,
      //   organics: this.resources.organics,
      //   canSpawn: canSpawn,
      //   alive: this.alive
      // });
    }
    
    // Track if we're blocked from spawning
    const wantsToSpawn = (this.resources.organics >= RESOURCE_CONFIG.ORGANICS_PER_UNIT) || (this.crystalUnitsToSpawn > 0);
    this.spawnBlocked = wantsToSpawn && !canSpawn;
    
    
    if (canSpawn) {
      // Check if we have a pending spawn request
      if (this.pendingSpawnRequest) {
        // Check if the promise has resolved
        if (this.pendingSpawnRequest.isResolved) {
          const spawnData = this.pendingSpawnRequest.result;
          if (spawnData) {
            // We got a soul! Create the soldier
            spawned = spawnData;
            
            // Consume resources based on spawn type
            if (this.pendingSpawnType === 'organic') {
              this.resources.organics -= RESOURCE_CONFIG.ORGANICS_PER_UNIT;
              debugLog('Base', {
                action: 'unit_spawned_from_organics',
                team: this.team.name,
                organicsRemaining: this.resources.organics,
                state: this.state,
                soulId: spawned.soulId
              });
            } else if (this.pendingSpawnType === 'crystal') {
              this.crystalUnitsToSpawn--;
              debugLog('Base', {
                action: 'unit_spawned_from_crystal',
                team: this.team.name,
                crystalUnitsRemaining: this.crystalUnitsToSpawn,
                soulId: spawned.soulId
              });
            }
            
            this.lastSpawnTime = now;
            this.lastActualSpawnTime = now;
          }
          
          // Clear the pending request
          this.pendingSpawnRequest = null;
          this.pendingSpawnType = null;
        }
        // If not resolved yet, just wait for next update
      } else {
        // No pending request, check if we need to start one
        const timeSinceLastSpawn = now - this.lastActualSpawnTime;
        const spawnCooldown = THRESHOLD_CONFIG.SPAWN_COOLDOWN; // 1 second between spawns
        
        if (this.resources.organics >= RESOURCE_CONFIG.ORGANICS_PER_UNIT && timeSinceLastSpawn >= spawnCooldown) {
          // Start async soul request without blocking
          this.startAsyncSpawn('organic');
        } else if (this.crystalUnitsToSpawn > 0) {
          // Start async soul request for crystal spawn
          this.startAsyncSpawn('crystal');
        }
      }
    }

    return spawned;
  }
  
  // Import statements for debug functions
  startAsyncSpawn(spawnType) {
    // Create a wrapper to track promise state
    const promiseWrapper = {
      isResolved: false,
      result: null
    };
    
    // Start the async soul request
    this.createSoulforgedSoldier().then(soldier => {
      promiseWrapper.isResolved = true;
      promiseWrapper.result = soldier;
    }).catch(error => {
      debugError(`[TeamBase] ${this.team.name} spawn request failed:`, error);
      promiseWrapper.isResolved = true;
      promiseWrapper.result = null;
    });
    
    this.pendingSpawnRequest = promiseWrapper;
    this.pendingSpawnType = spawnType;
    
    debugLog(`[TeamBase] ${this.team.name} started async ${spawnType} spawn request`);
  }
  
  async createSoulforgedSoldier() {
    try {
      // Import SoulforgeAPI dynamically to avoid circular dependency
      const { soulforgeAPI } = await import('../systems/SoulforgeAPI.js');

      // Request a real soul incarnation from the backend
      // This will wait for the API call to complete, but won't block the game loop
      // since this method is already async and the base update() properly awaits it
      const incarnationData = await soulforgeAPI.requestIncarnation('combat', [this.team.name]);

      // Check if we got a valid incarnation (not offline)
      // In offline mode, we accept offline souls since no real souls are available
      if (incarnationData.soul_id.startsWith('offline-') && !soulforgeAPI.offlineMode) {
        debugLog(`[TeamBase] ${this.team.name} base received offline soul, waiting for real soul...`);
        // For bases, we prefer to wait rather than spawn with offline souls
        // Return null to indicate spawning should be retried later
        return null;
      }
      
      // Create the soldier
      const soldier = new Soldier(this.x, this.y, this.canvas, this.team, this);
      
      // Apply soul modifiers to the soldier
      soulforgeAPI.applySoulModifiers(soldier, incarnationData);
      
      // Register the incarnation with the API for event tracking
      soulforgeAPI.registerIncarnation(soldier.id, incarnationData);
      
      debugLog('Soul', {
        action: 'soul_incarnated',
        soulId: incarnationData.soul_id,
        incarnationId: incarnationData.incarnation_id,
        team: this.team.name,
        modifiers: incarnationData.modifiers,
        hints: incarnationData.personality_hints,
        isOffline: false
      });
      
      return soldier;
    } catch (error) {
      debugError(`[TeamBase] ${this.team.name} base failed to create soulforged soldier:`, error);
      debugError('Full error details:', error.stack);
      // For network errors, return null to retry later rather than creating a soulless soldier
      return null;
    }
  }

  // Override takeDamage to handle base destruction with Robot3 pattern
  takeDamage(damage, attacker) {
    if (!this.alive) return false;

    this.health -= damage;
    this.damageIndicator = 1;
    audioManager.playHit();

    // Check if base is destroyed
    if (this.health <= 0) {
      this.health = 0;
      // Cancel any active defense request
      if (this.activeDefenseRequest) {
        teamRadioNetwork.cancelRequest(this.team.id, this.activeDefenseRequest.id);
        this.activeDefenseRequest = null;
      }
      // Send destroyed event to state machine instead of calling die() directly
      if (this.ai && this.ai.stateService) {
        // Import events here to avoid circular dependency
        this.ai.stateService.send('DESTROYED');
      }
      audioManager.playKill();
      return true; // Base was destroyed
    }

    // Notify AI of damage
    if (this.ai && this.ai.onDamageTaken) {
      this.ai.onDamageTaken(damage, attacker);
    }

    // Broadcast defense request if health drops below threshold
    this.#checkDefenseRequest();

    return false; // Base survived
  }

  /**
   * Check if base should broadcast a defense request
   * Called after taking damage to request help from nearby soldiers
   * @private
   */
  #checkDefenseRequest() {
    if (!RADIO_CONFIG.BASE_DEFENSE_ENABLED) return;
    if (!this.alive || !this.team) return;

    const healthRatio = this.health / this.maxHealth;
    const now = Date.now();

    // Only broadcast if below health threshold
    if (healthRatio >= RADIO_CONFIG.BASE_DEFENSE_HEALTH_THRESHOLD) {
      // Base is healthy, cancel any active request
      if (this.activeDefenseRequest) {
        teamRadioNetwork.cancelRequest(this.team.id, this.activeDefenseRequest.id);
        this.activeDefenseRequest = null;
      }
      return;
    }

    // Check cooldown to prevent spam
    if (now - this.lastDefenseRequestTime < RADIO_CONFIG.BASE_DEFENSE_REQUEST_INTERVAL) {
      return;
    }

    // Count nearby attackers
    const nearbyEnemies = this.ai?.nearbyEnemies || [];
    const attackerIds = nearbyEnemies.map(e => e.id);

    // Calculate threat level based on health and number of attackers
    const healthThreat = 1 - healthRatio; // Lower health = higher threat
    const attackerThreat = Math.min(nearbyEnemies.length / 5, 1); // Cap at 5 attackers
    const threatLevel = (healthThreat * 0.7) + (attackerThreat * 0.3);

    // Broadcast defense request
    this.activeDefenseRequest = teamRadioNetwork.broadcastDefenseRequest(this.team.id, {
      baseId: this.id,
      position: { x: this.x, y: this.y },
      threatLevel: threatLevel,
      attackerIds: attackerIds,
      attackerCount: nearbyEnemies.length
    });

    this.lastDefenseRequestTime = now;

    debugLog('TeamBase', {
      action: 'defense_request_broadcast',
      team: this.team.name,
      baseId: this.id,
      requestId: this.activeDefenseRequest?.id,
      healthRatio: Number(healthRatio.toFixed(2)),
      threatLevel: Number(threatLevel.toFixed(2)),
      attackers: nearbyEnemies.length
    });
  }

  onMissileKill() {
    // Called when one of this base's missiles kills an enemy
    this.missileKills++;
    this.perceptionRange += BASE_CONFIG.PERCEPTION_KILL_BONUS;

    // Harvest organics from kill (soul harvesting)
    this.resources.organics += BASE_CONFIG.ORGANICS_PER_MISSILE_KILL;

    debugLog('TeamBase', {
      action: 'missile_kill_harvest',
      team: this.team.name,
      organicsGained: BASE_CONFIG.ORGANICS_PER_MISSILE_KILL,
      totalOrganics: this.resources.organics,
      kills: this.missileKills
    });

    // Check if base should level up
    if (this.level < BASE_CONFIG.MAX_LEVEL &&
        this.missileKills % BASE_CONFIG.KILLS_PER_LEVEL === 0) {
      this.levelUp();
    }
  }

  levelUp() {
    if (this.level >= BASE_CONFIG.MAX_LEVEL) {
      return;
    }

    this.level++;

    // Stat increases (same pattern as soldiers, but only for base-relevant stats)
    this.maxHealth *= BASE_CONFIG.LEVEL_UP_MULTIPLIER;
    this.health = this.maxHealth; // Fully heal on level up
    this.damage *= BASE_CONFIG.LEVEL_UP_MULTIPLIER;

    // Improve fire rate (lower = faster)
    if (this.ai) {
      this.ai.fireRate = Math.max(
        200, // Minimum fire rate cap (5 shots/second)
        this.ai.fireRate * BASE_CONFIG.FIRE_RATE_IMPROVEMENT
      );
    }

    // Visual feedback
    audioManager.playLevelUp();

    debugLog('TeamBase', {
      action: 'level_up',
      team: this.team.name,
      newLevel: this.level,
      health: Math.floor(this.maxHealth),
      damage: this.damage.toFixed(1),
      fireRate: this.ai ? Math.floor(this.ai.fireRate) : 'N/A',
      missileKills: this.missileKills
    });
  }



  depositResource(type, amount) {
    if (type === 'crystal') {
      // Crystal cores grant instant units
      this.crystalUnitsToSpawn = (this.crystalUnitsToSpawn || 0) + amount;
      
      // Also grant ammo if enabled
      if (RESOURCE_CONFIG.CRYSTAL_GRANTS_AMMO) {
        this.ammo = Math.min(this.ammo + RESOURCE_CONFIG.CRYSTAL_AMMO_GRANTED, this.maxAmmo);
        
        debugLog('Base', {
          action: 'crystal_ammo_granted',
          team: this.team.name,
          ammoGranted: RESOURCE_CONFIG.CRYSTAL_AMMO_GRANTED,
          newAmmo: this.ammo
        });
      }
      
      if (this.ai && this.ai.onResourceDeposited) {
        this.ai.onResourceDeposited(type, amount);
      }
      return;
    }
    
    if (!this.resources[type]) {
      this.resources[type] = 0;
    }
    
    let processed = amount;
    if (type === 'metal') {
      processed *= RESOURCE_CONFIG.METAL_TO_AMMO;
    }
    
    if (type === 'metal') {
      this.resources[type] = Math.min(this.resources[type] + processed, this.maxMetal);
    } else if (type === 'organics') {
      // Add organics for unit spawning
      this.resources[type] += amount;
      debugLog('Base', {
        action: 'organics_deposited',
        team: this.team.name,
        amount: amount.toFixed(1),
        total: this.resources[type].toFixed(1),
        unitsAvailable: Math.floor(this.resources[type] / RESOURCE_CONFIG.ORGANICS_PER_UNIT)
      });
    } else {
      this.resources[type] += processed;
    }
    
    // Trigger delivery pulse animation
    this.deliveryPulse = 1.0;
    
    // Notify AI of resource deposit
    if (this.ai && this.ai.onResourceDeposited) {
      this.ai.onResourceDeposited(type, amount);
    }
  }

  depositAmmo(amount) {
    // Soldiers can donate their ammo to the base
    const ammoToAdd = Math.min(amount, this.maxAmmo - this.ammo);
    this.ammo += ammoToAdd;
    
    // Notify AI of ammo donation
    if (this.ai && this.ai.onAmmoDonated) {
      this.ai.onAmmoDonated(ammoToAdd);
    }
    
    return ammoToAdd; // Return how much was actually deposited
  }
  
  giveAmmo(soldier) {
    // Give ammo to a soldier from base reserves
    if (!soldier || this.ammo <= 0) {
      return;
    }
    
    const ammoNeeded = soldier.maxAmmo - soldier.ammo;
    const ammoToGive = Math.min(ammoNeeded, this.ammo, 10); // Give up to 10 ammo per update
    
    if (ammoToGive > 0) {
      soldier.ammo += ammoToGive;
      this.ammo -= ammoToGive;
    }
  }

  consumeResource(type, amount) {
    if (!this.resources[type]) return 0;
    const used = Math.min(this.resources[type], amount);
    this.resources[type] -= used;
    return used;
  }
  
  // Register a soldier as actively repairing this base
  addRepairer(soldier) {
    if (this.activeRepairers.size < BASE_CONFIG.MAX_CONCURRENT_REPAIRS) {
      this.activeRepairers.add(soldier);
      return true;
    }
    return false;
  }
  
  // Remove a soldier from active repairers
  removeRepairer(soldier) {
    this.activeRepairers.delete(soldier);
  }
  
  // Check if base needs repairs and has room for more repairers
  needsRepairers() {
    return this.health < this.maxHealth && this.activeRepairers.size < BASE_CONFIG.MAX_CONCURRENT_REPAIRS;
  }
  
  repair(delta) {
    const repairPerMs = this.maxHealth / BASE_CONFIG.REPAIR_TIME;
    this.health = Math.min(this.maxHealth, this.health + repairPerMs * delta);
  }

  // Check if a unit is inside the base radius
  isInside(unit) {
    const distance = getDistance(this, unit);
    return distance <= this.size;
  }

  // Check if a unit is at the base center
  isAtCenter(unit) {
    const distance = getDistance(this, unit);
    return distance <= BASE_CONFIG.CENTER_THRESHOLD;
  }

  // Override render for base-specific visualization
  render(ctx) {
    // Don't render if destroyed
    if (!this.alive) return;
    
    ctx.save();
    
    // Apply spawn scale
    const currentScale = this.isSpawning ? this.spawnScale : 1.0;
    
    // Base shrinks when damaged AND applies spawn scale
    const healthRatio = this.health / this.maxHealth;
    const drawSize = this.size * healthRatio * currentScale;
    
    // Apply delivery pulse (grows base slightly)
    const pulseScale = 1.0 + (this.deliveryPulse * 0.2); // Max 20% size increase
    const finalSize = drawSize * pulseScale;

    ctx.fillStyle = this.color;
    ctx.globalAlpha = UI_CONFIG.BASE_CIRCLE_ALPHA;
    ctx.beginPath();
    ctx.arc(this.x, this.y, finalSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1.0;

    // Draw a border (also pulses)
    ctx.strokeStyle = this.color;
    ctx.lineWidth = UI_CONFIG.BASE_BORDER_WIDTH;
    ctx.beginPath();
    ctx.arc(this.x, this.y, finalSize, 0, Math.PI * 2);
    ctx.stroke();
    
    // Draw delivery pulse ring
    if (this.deliveryPulse > 0) {
      ctx.strokeStyle = this.color;
      ctx.globalAlpha = this.deliveryPulse * 0.5;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(this.x, this.y, finalSize + (1 - this.deliveryPulse) * 20, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1.0;
    }
    
    // Draw repair pulse effect (green healing glow)
    if (this.repairPulse > 0) {
      ctx.strokeStyle = '#00ff00';
      ctx.globalAlpha = this.repairPulse * 0.3;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(this.x, this.y, finalSize + (this.repairPulse * 10), 0, Math.PI * 2);
      ctx.stroke();
      
      // Inner healing glow
      ctx.fillStyle = '#00ff00';
      ctx.globalAlpha = this.repairPulse * 0.1;
      ctx.beginPath();
      ctx.arc(this.x, this.y, finalSize * 0.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    }
    
    ctx.restore();

    // Debug mode: Show perception range circle
    if (isDebugMode()) {
      // Show perception range circle
      if (DEBUG_VISUALIZATION_CONFIG.showPerceptionRadius) {
        ctx.strokeStyle = this.color;
        ctx.globalAlpha = UI_CONFIG.BASE_PERCEPTION_ALPHA; // Slightly more transparent than soldier circles
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.perceptionRange, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }
      
      // Show weapon range circle
      if (DEBUG_VISUALIZATION_CONFIG.showWeaponRadius) {
        ctx.strokeStyle = '#f00';
        ctx.globalAlpha = UI_CONFIG.BASE_PERCEPTION_ALPHA;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.weaponRange, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
      }
      
      // Show base stats near base
      ctx.fillStyle = '#fff';
      ctx.font = UI_CONFIG.FONT_MEDIUM;
      ctx.textAlign = 'center';
      
      let yOffset = this.size + UI_CONFIG.BASE_STATS_OFFSET_Y;
      
      // Always show ammo count (missiles)
      const missileCount = Math.floor(this.ammo / BASE_CONFIG.MISSILE_AMMO_COST);
      ctx.fillText(`Missiles: ${missileCount}`, this.x, this.y + yOffset);
      yOffset += 10;
      
      // Show ammo percentage with color coding
      const ammoPercent = (this.ammo / this.maxAmmo * 100).toFixed(0);
      ctx.fillStyle = this.ammo < this.maxAmmo * UI_CONFIG.AMMO_LOW_THRESHOLD ? UI_CONFIG.HEALTH_LOW_COLOR : '#fff';
      ctx.fillText(`Ammo: ${ammoPercent}%`, this.x, this.y + yOffset);
      yOffset += 10;
      
      if (this.missileKills > 0) {
        ctx.fillStyle = '#fff';
        ctx.fillText(`Kills: ${this.missileKills}`, this.x, this.y + yOffset);
        yOffset += 10;
        ctx.fillText(`Perception: ${this.perceptionRange.toFixed(0)}`, this.x, this.y + yOffset);
      }
    }

    // Draw level indicator above base (similar to soldiers)
    ctx.fillStyle = '#fff';
    ctx.font = UI_CONFIG.FONT_MEDIUM;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const levelText = this.level > BASE_CONFIG.STARTING_LEVEL ? `BASE L${this.level}` : `BASE`;
    ctx.fillText(levelText, this.x, this.y - drawSize - 25);

    // Draw organics count above base
    ctx.fillStyle = '#ff66cc';
    ctx.font = UI_CONFIG.FONT_LARGE;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const organics = Math.floor(this.resources.organics || 0);
    ctx.fillText(`⚗${organics}`, this.x, this.y - drawSize - 5);
    
    // Draw crystal units queued if any
    if (this.crystalUnitsToSpawn > 0) {
      ctx.fillStyle = '#00ffff';
      ctx.fillText(`✦${this.crystalUnitsToSpawn}`, this.x + drawSize + 10, this.y);
    }
    
    // Show if spawning is blocked due to unit limit
    if (this.spawnBlocked) {
      ctx.fillStyle = UI_CONFIG.HEALTH_LOW_COLOR;
      ctx.font = UI_CONFIG.FONT_MEDIUM;
      ctx.textAlign = 'center';
      ctx.fillText('UNIT LIMIT', this.x, this.y + drawSize + UI_CONFIG.BASE_STATS_OFFSET_Y);
    }
    
    // Show current priority/state
    if (this.priority !== 'hunt') {
      ctx.fillStyle = '#ffff00';
      ctx.font = UI_CONFIG.FONT_MEDIUM;
      ctx.textAlign = 'center';
      ctx.fillText(`[${this.priority}]`, this.x, this.y - drawSize - UI_CONFIG.BASE_STATS_OFFSET_Y);
    }
    
    // Show ammo regeneration indicator
    if (BASE_CONFIG.ENABLE_AMMO_REGEN && this.ammo < this.maxAmmo * BASE_CONFIG.CRITICAL_AMMO_THRESHOLD) {
      ctx.fillStyle = '#00ff00';
      ctx.font = UI_CONFIG.FONT_SMALL;
      ctx.textAlign = 'center';
      ctx.fillText('REGEN', this.x, this.y);
    }
    
    // Show active repairers count
    if (this.activeRepairers.size > 0) {
      ctx.fillStyle = '#00ff00';
      ctx.font = UI_CONFIG.FONT_SMALL;
      ctx.textAlign = 'center';
      ctx.fillText(`REPAIR x${this.activeRepairers.size}`, this.x, this.y + 15);
    }
    
    // Draw team name
    // ctx.fillStyle = '#fff';
    // ctx.font = '12px monospace';
    // ctx.textAlign = 'center';
    // ctx.textBaseline = 'middle';
    // ctx.fillText(this.team.name, this.x, this.y);
    
    // Let AI render debug info
    if (this.ai && this.ai.renderDebug) {
      this.ai.renderDebug(ctx);
    }

    // Show state text in debug mode (same as soldiers)
    if (isDebugMode() && DEBUG_VISUALIZATION_CONFIG.showUnitState) {
      ctx.fillStyle = DEBUG_VISUALIZATION_CONFIG.colors.unitStateText;
      ctx.font = UI_CONFIG.FONT_MEDIUM;
      ctx.textAlign = 'left';
      ctx.fillText(this.state, this.x + drawSize + UI_CONFIG.STATE_TEXT_OFFSET_X, this.y);
    }

    // Draw resource tractor beams (metal/crystal collection visualization)
    if (this.tractorBeamResources && this.tractorBeamResources.length > 0) {
      for (const resource of this.tractorBeamResources) {
        // Pulsing beam effect
        const pulsePhase = (Date.now() % 1000) / 1000; // 0-1 cycle per second
        const pulseAlpha = 0.15 + Math.sin(pulsePhase * Math.PI * 2) * 0.1;

        // Beam from base to resource
        ctx.save();
        ctx.globalAlpha = pulseAlpha;
        // Blue for metal, cyan for crystal
        ctx.strokeStyle = resource instanceof Crystal ? '#00ffff' : '#66b3ff';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);

        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(resource.x, resource.y);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.restore();

        // Glow around resource being pulled
        ctx.save();
        ctx.globalAlpha = pulseAlpha * 1.5;
        ctx.strokeStyle = resource instanceof Crystal ? '#00ffff' : '#66b3ff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(resource.x, resource.y, 6, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Draw corpse tractor beams (soul harvesting visualization)
    if (this.tractorBeamCorpses && this.tractorBeamCorpses.length > 0) {
      for (const corpse of this.tractorBeamCorpses) {
        // Pulsing beam effect
        const pulsePhase = (Date.now() % 1000) / 1000; // 0-1 cycle per second
        const pulseAlpha = 0.1 + Math.sin(pulsePhase * Math.PI * 2) * 0.05;

        // Beam from base to corpse
        ctx.save();
        ctx.globalAlpha = pulseAlpha;
        ctx.strokeStyle = '#ff66cc'; // Pink for organics/souls
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);

        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(corpse.x, corpse.y);
        ctx.stroke();

        ctx.setLineDash([]);
        ctx.restore();

        // Glow around corpse being pulled
        ctx.save();
        ctx.globalAlpha = pulseAlpha * 2;
        ctx.strokeStyle = '#ff66cc';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(corpse.x, corpse.y, 8, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }
  }
}
